import { useState } from 'react';
import { useNotify } from 'react-admin';
import {
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { toErrorMessage } from '../userOffers/orderActionsApi';
import {
  AUTHOR_LABELS,
  BASIS_LABELS,
  PERIOD_LABELS,
  SCOPE_LABELS,
  choicesOf,
  createType,
  dictionaryLabel,
  updateType,
  type FuelSurchargeType,
} from './fuelSurchargesApi';

/**
 * Rodzaje korekt (B4): nazwa, autor, okres, moment naliczenia, zakres,
 * uwagi. Startowe to wartości robocze z Odpowiedzi CS — bez nazw klientów;
 * korektę konkretnego klienta CS tworzy z wariantu. Rodzaju się nie usuwa
 * (jest w historii i w plikach od CS) — wyłącza.
 */

type Form = {
  name: string;
  author: string;
  period: string;
  basis: string;
  scope: string;
  notes: string;
  active: boolean;
  sortOrder: string;
};

const EMPTY: Form = { name: '', author: '', period: '', basis: '', scope: '', notes: '', active: true, sortOrder: '500' };

const formOf = (t: FuelSurchargeType): Form => ({
  name: t.name,
  author: t.author ?? '',
  period: t.period ?? '',
  basis: t.basis ?? '',
  scope: t.scope ?? '',
  notes: t.notes ?? '',
  active: t.active,
  sortOrder: String(t.sortOrder),
});

/** Zmiana w oknie — tylko pola, które się różnią; `null` = nic do zapisu. */
export function typeChangesOf(t: FuelSurchargeType, f: Form): Partial<FuelSurchargeType> | null {
  const out: Record<string, unknown> = {};
  const before = formOf(t);
  for (const k of ['name', 'author', 'period', 'basis', 'scope', 'notes'] as const) {
    const next = f[k].trim();
    if (next !== before[k].trim()) out[k] = next === '' ? null : next;
  }
  if (f.active !== t.active) out.active = f.active;
  const order = Number(f.sortOrder);
  if (f.sortOrder.trim() !== '' && Number.isInteger(order) && order !== t.sortOrder) out.sortOrder = order;
  return Object.keys(out).length > 0 ? (out as Partial<FuelSurchargeType>) : null;
}

const DictionaryField = ({
  label,
  labels,
  value,
  onChange,
}: {
  label: string;
  labels: Record<string, string>;
  value: string;
  onChange: (v: string) => void;
}) => (
  <TextField select label={label} value={value} onChange={(e) => onChange(e.target.value)} sx={{ minWidth: 200, flex: 1 }}>
    {choicesOf(labels).map((c) => (
      <MenuItem key={c.id || 'none'} value={c.id}>
        {c.name}
      </MenuItem>
    ))}
  </TextField>
);

export const TypesTab = ({
  types,
  canEdit,
  onChanged,
}: {
  types: FuelSurchargeType[];
  canEdit: boolean;
  onChanged: () => void;
}) => {
  const notify = useNotify();
  const [editing, setEditing] = useState<{ row: FuelSurchargeType | null; form: Form } | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const open = (row: FuelSurchargeType | null) => {
    setReason('');
    setEditing({ row, form: row ? formOf(row) : EMPTY });
  };
  const pending = editing?.row ? typeChangesOf(editing.row, editing.form) : null;
  const canSave =
    !!editing && reason.trim() !== '' && (editing.row ? pending !== null : editing.form.name.trim() !== '');

  const save = async () => {
    if (!editing) return;
    setBusy(true);
    try {
      if (editing.row) {
        await updateType(editing.row.id, { ...(pending ?? {}), reason: reason.trim() });
      } else {
        const f = editing.form;
        await createType({
          name: f.name.trim(),
          author: f.author || null,
          period: f.period || null,
          basis: f.basis || null,
          scope: f.scope || null,
          notes: f.notes.trim() || null,
          sortOrder: Number(f.sortOrder) || 500,
          reason: reason.trim(),
        });
      }
      notify('Zapisano', { type: 'success' });
      setEditing(null);
      onChanged();
    } catch (e) {
      notify(toErrorMessage(e), { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const set = (patch: Partial<Form>) => editing && setEditing({ ...editing, form: { ...editing.form, ...patch } });

  return (
    <>
      {canEdit && (
        <Stack direction="row" sx={{ justifyContent: 'flex-end', mb: 1 }}>
          <Button variant="outlined" onClick={() => open(null)}>
            Dodaj rodzaj
          </Button>
        </Stack>
      )}
      <Table size="small" data-testid="fuel-types">
        <TableHead>
          <TableRow>
            <TableCell>Rodzaj korekty</TableCell>
            <TableCell>Autor</TableCell>
            <TableCell>Okres</TableCell>
            <TableCell>Moment</TableCell>
            <TableCell>Zakres</TableCell>
            <TableCell align="right">Przypisań</TableCell>
            <TableCell align="right">Wartości</TableCell>
            {canEdit && <TableCell />}
          </TableRow>
        </TableHead>
        <TableBody>
          {types.map((t) => (
            <TableRow key={t.id} data-testid={`fuel-type-${t.code}`}>
              <TableCell>
                {t.name}
                {t.active ? null : <Chip size="small" variant="outlined" label="wyłączony" sx={{ ml: 1 }} />}
                {t.notes ? (
                  <Typography variant="caption" color="text.secondary" component="div">
                    {t.notes}
                  </Typography>
                ) : null}
              </TableCell>
              <TableCell>{dictionaryLabel(AUTHOR_LABELS, t.author)}</TableCell>
              <TableCell>{dictionaryLabel(PERIOD_LABELS, t.period)}</TableCell>
              <TableCell>{dictionaryLabel(BASIS_LABELS, t.basis)}</TableCell>
              <TableCell>{dictionaryLabel(SCOPE_LABELS, t.scope)}</TableCell>
              <TableCell align="right">{t.assignmentCount ?? 0}</TableCell>
              <TableCell align="right">{t.valueCount ?? 0}</TableCell>
              {canEdit && (
                <TableCell align="right">
                  <Button size="small" onClick={() => open(t)}>
                    Zmień
                  </Button>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog open={editing !== null} onClose={() => setEditing(null)} fullWidth maxWidth="md">
        <DialogTitle>{editing?.row ? `Zmień: ${editing.row.name}` : 'Dodaj rodzaj korekty'}</DialogTitle>
        <DialogContent>
          {editing && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label="Nazwa"
                required
                value={editing.form.name}
                onChange={(e) => set({ name: e.target.value })}
                slotProps={{ htmlInput: { maxLength: 200 } }}
              />
              <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
                <DictionaryField label="Autor" labels={AUTHOR_LABELS} value={editing.form.author} onChange={(v) => set({ author: v })} />
                <DictionaryField label="Okres" labels={PERIOD_LABELS} value={editing.form.period} onChange={(v) => set({ period: v })} />
              </Stack>
              <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
                <DictionaryField
                  label="Moment naliczenia"
                  labels={BASIS_LABELS}
                  value={editing.form.basis}
                  onChange={(v) => set({ basis: v })}
                />
                <DictionaryField label="Zakres" labels={SCOPE_LABELS} value={editing.form.scope} onChange={(v) => set({ scope: v })} />
              </Stack>
              <TextField
                label="Uwagi"
                multiline
                minRows={2}
                value={editing.form.notes}
                onChange={(e) => set({ notes: e.target.value })}
                slotProps={{ htmlInput: { maxLength: 1000 } }}
              />
              <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
                <TextField
                  label="Kolejność"
                  type="number"
                  value={editing.form.sortOrder}
                  onChange={(e) => set({ sortOrder: e.target.value })}
                  slotProps={{ htmlInput: { min: 0, max: 9999 } }}
                  sx={{ width: 140 }}
                />
                {editing.row && (
                  <FormControlLabel
                    control={<Checkbox checked={editing.form.active} onChange={(e) => set({ active: e.target.checked })} />}
                    label="W użyciu (do wyboru przy przypisaniu)"
                  />
                )}
              </Stack>
              <TextField
                label="Powód zmiany"
                required
                multiline
                minRows={2}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                slotProps={{ htmlInput: { maxLength: 500 } }}
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)}>Anuluj</Button>
          <Button variant="contained" disabled={!canSave || busy} onClick={() => void save()}>
            Zapisz
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
